<div class="bk-page sim">
  <header class="bk-page__head">
    <div class="bk-page__intro">
      <p class="bk-eyebrow">USSD</p>
      <h1 class="bk-page__title">Simulator</h1>
      <p class="bk-page__sub">
        Dial your service codes from a test handset. Every request and response is logged below with its timing.
      </p>
    </div>
    <div class="bk-page__actions">
      <a class="bk-btn bk-btn--ghost" routerLink="/organization/ussd-simulator/applications">
        <span class="bk-sym" aria-hidden="true">widgets</span>
        Applications
      </a>
    </div>
  </header>

  <div class="sim__grid">
    <!-- ── Handset ── -->
    <div class="sim__phone">
      <app-phone-frame variant="console">
        @if (screen === 'ussd') {
          <app-ussd-dialog
            [message]="ussdMessage"
            [dialedCode]="dialedCode"
            [isLoading]="isLoading"
            [isSessionEnd]="isSessionEnd"
            [isError]="isError"
            [carrier]="carrier"
            (send)="store.handleUSSDSend($event)"
            (cancel)="store.handleUSSDCancel()"
            (home)="goHome()"
          />
        } @else {
          <app-dialer-screen
            [dialedCode]="dialedCode"
            [isLoading]="isLoading"
            [carrier]="carrier"
            [hint]="dialHint"
            (digitPress)="store.handleDigitPress($event)"
            (delete)="store.handleDelete()"
            (codeChange)="onCodeChange($event)"
            (call)="dial()"
            (home)="goHome()"
          />
        }
      </app-phone-frame>
      <p class="sim__phone-note">Type on your keyboard too: digits, * and #. Enter dials or sends.</p>
    </div>

    <!-- ── Controls ── -->
    <div class="sim__side">
      <section class="bk-panel sim__card" aria-labelledby="sim-number-title">
        <h2 class="bk-eyebrow sim__card-title" id="sim-number-title">Test handset</h2>
        <app-phone-input
          [value]="phoneNumber"
          [country]="selectedCountry"
          [countries]="countries"
          [disabled]="sessionLive"
          (valueChange)="onNumberChange($event)"
          (countryChange)="onCountryChange($event)"
        />

        <div class="bk-field sim__operator">
          <label class="bk-field__label" for="sim-operator">Network</label>
          <select
            id="sim-operator"
            class="bk-field__input"
            [disabled]="sessionLive || operatorsState !== 'ready'"
            [value]="operator"
            (change)="onOperatorChange($event)"
          >
            <option value="">Detect from the number</option>
            @for (op of operators; track op.code) {
              <option [value]="op.code" [selected]="op.code === operator">{{ op.name }}</option>
            }
          </select>
          <p class="bk-field__hint">
            @switch (operatorsState) {
              @case ('loading') { Looking up networks in {{ selectedCountry.name }}… }
              @case ('none') { The simulator has no network list for {{ selectedCountry.name }}, so it detects one from the number. }
              @default { Pick a network to test its routing rules. }
            }
          </p>
        </div>

        @if (msisdn) {
          <p class="sim__msisdn">
            Sends as <strong>{{ msisdn }}</strong>
          </p>
        }
        @if (sessionLive) {
          <p class="sim__locked">End the session to change the handset.</p>
        }
      </section>

      <section class="bk-panel sim__card" aria-labelledby="sim-session-title">
        <div class="sim__card-head">
          <h2 class="bk-eyebrow sim__card-title" id="sim-session-title">Session</h2>
          <app-status [text]="sessionView.text" [status]="sessionView.status" />
        </div>

        @if (sessionStartedAt || sessionId) {
          <dl class="sim__facts">
            <div>
              <dt>Code</dt>
              <dd class="sim__code">{{ store.currentServiceCode || dialedCode || '—' }}</dd>
            </div>
            @if (dialedApp; as app) {
              <div>
                <dt>Application</dt>
                <dd>{{ app.name }} <span class="sim__quiet">{{ environmentLabel(app.environment) }}</span></dd>
              </div>
            }
            @if (sessionId) {
              <div class="sim__fact-wide">
                <dt>Session ID</dt>
                <dd class="sim__id">
                  <span>{{ sessionId }}</span>
                  <button type="button" class="bk-icon-btn sim__copy" aria-label="Copy session ID" matTooltip="Copy session ID" (click)="copySessionId()">
                    <span class="bk-sym" aria-hidden="true">content_copy</span>
                  </button>
                </dd>
              </div>
            }
            @if (roundTrip) {
              <div>
                <dt>Progress</dt>
                <dd>{{ roundTrip }}</dd>
              </div>
            }
          </dl>
        } @else {
          <p class="sim__empty-line">Nothing dialled yet. Pick a code below or type one on the handset.</p>
        }

        @if (countryMismatch) {
          <p class="sim__note">
            <span class="sim__dot sim__dot--wait" aria-hidden="true"></span>
            This code is registered in {{ countryMismatch }} and your test number is in {{ selectedCountry.name }}.
          </p>
        }
        @if (error) {
          <p class="sim__note" role="alert">
            <span class="sim__dot sim__dot--bad" aria-hidden="true"></span>
            {{ error }}
          </p>
        }

        @if (sessionLive || sessionState === 'ended' || sessionState === 'failed') {
          <div class="sim__card-actions">
            @if (sessionLive) {
              <button type="button" class="bk-btn bk-btn--ghost" (click)="endSession()">
                <span class="bk-sym" aria-hidden="true">call_end</span>
                End session
              </button>
            } @else {
              <button type="button" class="bk-btn bk-btn--ghost" [disabled]="!phoneNumber" (click)="redial()">
                <span class="bk-sym" aria-hidden="true">replay</span>
                Dial {{ store.currentServiceCode || dialedCode }} again
              </button>
            }
          </div>
        }
      </section>

      <section class="bk-panel sim__card sim__card--flush" aria-labelledby="sim-quick-title">
        <div class="sim__card-head sim__card-head--inset">
          <h2 class="bk-eyebrow sim__card-title" id="sim-quick-title">Your codes</h2>
        </div>
        @switch (appsState) {
          @case ('loading') {
            <div class="sim__skel" aria-hidden="true">
              <span class="bk-skel"></span>
              <span class="bk-skel"></span>
              <span class="bk-skel"></span>
            </div>
          }
          @case ('error') {
            <p class="sim__empty-line sim__empty-line--inset">Your applications could not be loaded. You can still type any code on the handset.</p>
          }
          @default {
            @if (apps.length) {
              <ul class="sim__apps">
                @for (app of apps; track app.id) {
                  <li>
                    <button
                      type="button"
                      class="sim__app"
                      [disabled]="isLoading"
                      [attr.aria-label]="'Dial ' + app.serviceCode + ', ' + app.name"
                      (click)="quickDial(app)"
                    >
                      <span class="sim__app-dot" [class.is-off]="app.enabled === false" aria-hidden="true"></span>
                      <span class="sim__app-text">
                        <span class="sim__app-name">{{ app.name }}</span>
                        <span class="sim__app-meta">
                          {{ environmentLabel(app.environment) }}{{ app.enabled === false ? ', disabled' : '' }}
                        </span>
                      </span>
                      <span class="sim__app-code">{{ app.serviceCode }}</span>
                      <span class="bk-sym sim__app-go" aria-hidden="true">call</span>
                    </button>
                  </li>
                }
              </ul>
            } @else {
              <p class="sim__empty-line sim__empty-line--inset">
                No applications yet. Create one to get a code, or type any code on the handset.
              </p>
            }
          }
        }
      </section>
    </div>
  </div>

  <!-- ── Transcript and request inspector ── -->
  <section class="bk-panel sim__log" aria-labelledby="sim-log-title">
    <div class="sim__log-head">
      <div>
        <h2 class="bk-section-title" id="sim-log-title">Transcript</h2>
        <p class="sim__log-sub">Newest first. Each screen is checked against the 182-character USSD limit.</p>
      </div>
      <div class="sim__log-actions">
        <button type="button" class="bk-btn bk-btn--ghost" [disabled]="!turns.length" (click)="downloadTranscript()">
          <span class="bk-sym" aria-hidden="true">download</span>
          Download CSV
        </button>
        <button type="button" class="bk-btn bk-btn--ghost" [disabled]="!turns.length" (click)="clearTranscript()">
          Clear transcript
        </button>
      </div>
    </div>

    <!-- Always rendered, so the first turn is announced too -->
    <p class="bk-sr-only" role="status">{{ transcriptStatus }}</p>

    @if (turns.length) {
      <ol class="turns">
        @for (turn of turns; track turn.id) {
          <li class="turn" [attr.data-kind]="turn.kind" [class.is-failed]="!!turn.error">
            <div class="turn__head">
              <p class="turn__label">
                <span class="bk-sym turn__icon" aria-hidden="true">
                  {{ turn.kind === 'New' ? 'call' : turn.kind === 'Release' ? 'call_end' : 'reply' }}
                </span>
                {{ turn.label }}
              </p>
              <p class="turn__meta">
                <span>{{ turn.time }}</span>
                @if (turn.pending) {
                  <span class="turn__wait">Waiting…</span>
                } @else if (turn.latency) {
                  <span>{{ turn.latency }}</span>
                }
              </p>
            </div>

            @if (turn.error) {
              <p class="turn__error">
                <span class="sim__dot sim__dot--bad" aria-hidden="true"></span>
                {{ turn.error }}
              </p>
            }

            @if (turn.responseText && turn.kind !== 'Release') {
              <div class="turn__screen">
                <p class="turn__text">{{ turn.responseText }}</p>
              </div>
            }

            <div class="turn__foot">
              @if (turn.responseType) {
                <span class="turn__chip">{{ turn.responseType === 'Release' ? 'Session ended by the application' : turn.responseType === 'Timeout' ? 'Timed out' : 'Waiting for input' }}</span>
              }
              @if (turn.check; as check) {
                <span class="turn__check" [class.is-over]="check.over">
                  <span class="sim__dot" [class.sim__dot--bad]="check.over" [class.sim__dot--ok]="!check.over" aria-hidden="true"></span>
                  {{ check.summary }}{{ check.over ? '. Handsets cut the rest.' : '' }}
                </span>
                @if (check.offender) {
                  <span class="turn__check">Uses UCS-2 because of {{ check.offender }}, so the limit is {{ check.limit }}.</span>
                }
              }
              <button
                type="button"
                class="bk-more turn__toggle"
                [attr.aria-expanded]="openTurns.has(turn.id)"
                [attr.aria-controls]="'turn-raw-' + turn.id"
                (click)="toggleTurn(turn.id)"
              >
                {{ openTurns.has(turn.id) ? 'Hide request and response' : 'Show request and response' }}
                <span class="bk-sym turn__chev" [class.is-open]="openTurns.has(turn.id)" aria-hidden="true">expand_more</span>
              </button>
            </div>

            @if (openTurns.has(turn.id)) {
              <div class="turn__raw" [id]="'turn-raw-' + turn.id">
                <div>
                  <p class="bk-eyebrow">Request</p>
                  <pre class="turn__json" tabindex="0" aria-label="Request JSON">{{ turn.requestJson }}</pre>
                </div>
                <div>
                  <p class="bk-eyebrow">Response</p>
                  <pre class="turn__json" tabindex="0" aria-label="Response JSON">{{ turn.responseJson }}</pre>
                </div>
              </div>
            }
          </li>
        }
      </ol>
    } @else {
      <div class="sim__log-empty">
        <span class="bk-sym" aria-hidden="true">receipt_long</span>
        <p>Nothing logged yet. Dial a code and each request and response appears here with its timing.</p>
      </div>
    }
  </section>
</div>
